<template>
  <div class="nexus-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Page Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="nexus-title">{{ isViewOnly ? 'VIEW SHIPMENT' : 'EDIT SHIPMENT' }}</h1>
      </v-row>

      <!-- View-Only Banner -->
      <v-row v-if="isViewOnly" justify="center" class="mb-4">
        <v-col cols="12" md="8">
          <v-alert
            type="info"
            prominent
            border="left"
            class="nexus-alert"
          >
            <v-row align="center">
              <v-col class="grow">
                <div class="font-weight-medium">Read-Only Mode</div>
                <div class="text-caption">This shipment is delivered and cannot be modified. View for tracking purposes only.</div>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Form Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="11" lg="10" class="pa-0">
          <v-card elevation="0" class="nexus-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
              </v-overlay>

              <v-form ref="form" v-model="valid">
                <!-- Shipment Details Header -->
                <div class="section-header mb-6">
                  <h2 class="section-title">SHIPMENT DETAILS</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Row 1: Basic shipment info -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="shipmentNumber"
                      label="Shipment Number"
                      dense outlined readonly
                      prepend-inner-icon="mdi-numeric"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="createdDate"
                      label="Created Date"
                      readonly dense outlined
                      prepend-inner-icon="mdi-calendar"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="shipmentOrderNumber"
                      label="Shipment Order Number"
                      dense outlined
                      prepend-inner-icon="mdi-note-text"
                      :readonly="isOpenOrderShipment || isViewOnly"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-text-field
                      v-model="poNumber"
                      label="PO Number"
                      dense outlined
                      prepend-inner-icon="mdi-file-document"
                      :rules="[rules.required]"
                      :readonly="isOpenOrderShipment || isViewOnly"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 2: Supplier, dates, method, status -->
                <v-row class="mb-6">
                  <v-col cols="12" md="6" lg="3">
                    <v-autocomplete
                      v-model="supplierName"
                      :items="supplierOptions"
                      item-text="supplierName"
                      item-value="supplierName"
                      label="Supplier"
                      dense outlined
                      prepend-inner-icon="mdi-factory"
                      :rules="[rules.required]"
                      :readonly="isOpenOrderShipment || isViewOnly"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-menu
                      ref="arrivalDateMenu"
                      v-model="arrivalDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                      :disabled="isViewOnly"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="arrivalDate"
                          label="Expected Arrival Date"
                          readonly dense outlined
                          prepend-inner-icon="mdi-calendar-clock"
                          v-bind="attrs" v-on="on"
                          :rules="[rules.required]"
                          :disabled="isOpenOrderShipment || isViewOnly"
                          class="nexus-input"
                        />
                      </template>
                      <v-date-picker
                        v-model="arrivalDate"
                        @input="arrivalDateMenu = false"
                        color="primary"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-select
                      v-model="method"
                      :items="methodOptions"
                      label="Method"
                      dense outlined
                      prepend-inner-icon="mdi-truck-delivery"
                      :rules="[rules.required]"
                      :readonly="isOpenOrderShipment || isViewOnly"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="6" lg="3">
                    <v-select
                      v-model="status"
                      :items="statusOptions"
                      label="Status"
                      dense outlined
                      prepend-inner-icon="mdi-information"
                      :rules="[rules.required]"
                      @change="updateLinkedSOStatuses"
                      :readonly="isOpenOrderShipment || isViewOnly"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Linked SO Numbers Section -->
                <div class="section-header mb-4">
                  <h3 class="section-title">LINKED SO NUMBERS</h3>
                  <div class="section-divider"></div>
                </div>

                <v-row class="mb-6">
                  <v-col cols="12">
                    <div class="d-flex justify-space-between align-center mb-3">
                      <span class="text-subtitle-2 font-weight-medium">Sales Orders in this Shipment</span>
                      <div class="d-flex gap-2" v-if="!isViewOnly">
                        <v-btn 
                          color="secondary" 
                          outlined
                          @click="refreshTableData"
                          class="nexus-btn-outlined"
                          small
                        >
                          <v-icon left small>mdi-refresh</v-icon>
                          Refresh
                        </v-btn>
                        <v-btn 
                          color="primary" 
                          outlined
                          @click="openSOManagementDialog"
                          :disabled="isOpenOrderShipment"
                          class="nexus-btn-outlined"
                          small
                        >
                          <v-icon left small>mdi-cog</v-icon>
                          Manage SOs
                        </v-btn>
                      </div>
                    </div>
                    
                    <v-data-table
                      :headers="soTableHeaders"
                      :items="linkedSODetails"
                      class="nexus-table elevation-2"
                      :items-per-page="15"
                      :footer-props="{
                        'items-per-page-options': [15],
                        'show-current-page': true
                      }"
                      no-data-text="No SOs linked to this shipment"
                    >
                      <template v-slot:item.salesOrderNumber="{ item }">
                        <v-chip color="primary" dark small class="nexus-chip">
                          SO: {{ item.salesOrderNumber }}
                        </v-chip>
                      </template>
                      <template v-slot:item.customerName="{ item }">
                        <span>{{ item.customerName || 'Unknown Customer' }}</span>
                      </template>
                      <template v-slot:item.status="{ item }">
                        <v-chip :color="getStatusColor(item.status)" dark small>
                          {{ item.status }}
                        </v-chip>
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-btn
                          v-if="!isOpenOrderShipment && !isViewOnly"
                          icon
                          @click="confirmRemoveSOFromShipment(item.salesOrderNumber)"
                          color="error"
                          small
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                        <span v-else-if="isViewOnly" class="text-caption text--secondary">Read Only</span>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>

                <!-- Supplier Updates Section -->
                <div class="section-header mb-4">
                  <h3 class="section-title">SUPPLIER UPDATES</h3>
                  <div class="section-divider"></div>
                </div>

                <v-row v-if="!isViewOnly" class="mb-4 align-center">
                  <v-col cols="12" md="9">
                    <v-textarea
                      v-model="newSupplierUpdate"
                      label="Add Supplier Update"
                      auto-grow
                      rows="2"
                      dense outlined
                      prepend-inner-icon="mdi-comment-text"
                      :disabled="isOpenOrderShipment"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn 
                      color="primary" 
                      @click="confirmAddSupplierUpdate"
                      :disabled="isOpenOrderShipment"
                      class="nexus-btn"
                      block
                    >
                      <v-icon left>mdi-plus</v-icon>Add Update
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Updates Table -->
                <v-row v-if="latestUpdateFromSupplier.length" class="mb-6">
                  <v-col cols="12">
                    <div class="section-header mb-3">
                      <h4 class="section-title" style="font-size: 1.1rem;">AUDIT LOG</h4>
                      <div class="section-divider"></div>
                    </div>
                    <v-data-table
                      :headers="tableHeaders"
                      :items="latestUpdateFromSupplier"
                      class="nexus-table elevation-2"
                      sort-by="timestamp"
                      sort-desc
                      :items-per-page="10"
                      :footer-props="{
                        'items-per-page-options': [5, 10, 15, 25],
                        'show-current-page': true,
                        'show-first-last-page': true
                      }"
                    >
                      <template v-slot:item.timestamp="{ item }">
                        <div class="text-no-wrap">
                          <div class="font-weight-medium">{{ formatDate(item.timestamp) }}</div>
                          <div class="text-caption text--secondary">{{ formatTime(item.timestamp) }}</div>
                        </div>
                      </template>
                      <template v-slot:item.text="{ item }">
                        <div class="text-wrap" style="max-width: 400px;">
                          {{ item.text }}
                        </div>
                      </template>
                      <template v-slot:item.user="{ item }">
                        <v-chip small outlined color="primary">
                          <v-icon left small>mdi-account</v-icon>
                          {{ item.user || 'System' }}
                        </v-chip>
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-btn
                          v-if="isAdmin && !isOpenOrderShipment && !isViewOnly"
                          icon
                          @click="removeUpdate(latestUpdateFromSupplier.indexOf(item))"
                          color="error"
                          small
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                        <span v-else-if="isViewOnly" class="text-caption text--secondary">Read Only</span>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>

                <!-- Save Button -->
                <v-row v-if="!isOpenOrderShipment && !isViewOnly" justify="center" class="mt-8">
                  <v-btn
                    color="primary"
                    large
                    width="300px"
                    @click="confirmSubmit"
                    :disabled="!valid || loading"
                    class="nexus-btn-primary"
                  >
                    <v-icon left>mdi-content-save</v-icon>Save Changes
                  </v-btn>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- SO Management Dialog -->
    <v-dialog v-model="soManagementDialog.visible" max-width="700">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">
          <v-icon left color="primary">mdi-truck-fast</v-icon>
          Manage Linked SOs
        </v-card-title>
        <v-card-text>
          <v-tabs v-model="soManagementDialog.activeTab" color="primary">
            <v-tab>Currently Linked</v-tab>
            <v-tab>Add SOs</v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="soManagementDialog.activeTab">
            <!-- Current SOs Tab -->
            <v-tab-item>
              <v-list class="mt-4">
                <v-list-item 
                  v-for="so in linkedSONumbers" 
                  :key="so"
                  class="mb-2"
                >
                  <v-list-item-content>
                    <v-list-item-title>SO #{{ so }}</v-list-item-title>
                    <v-list-item-subtitle>Customer: {{ getSOSupplier(so) }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn 
                      icon 
                      color="error" 
                      @click="confirmRemoveSOFromShipment(so)"
                      :disabled="isOpenOrderShipment"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-tab-item>
            
            <!-- Add SOs Tab -->
            <v-tab-item>
              <v-autocomplete
                v-model="soManagementDialog.newSOs"
                :items="availableSOs"
                item-text="soDisplay"
                item-value="salesOrderNumber"
                label="Add SO Numbers"
                multiple
                chips
                dense
                outlined
                class="mt-4 nexus-input"
              />
              <v-btn 
                color="primary" 
                @click="addSOsToShipment"
                :disabled="!soManagementDialog.newSOs.length"
                class="nexus-btn"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Selected SOs
              </v-btn>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" text @click="soManagementDialog.visible = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="confirmationDialog.confirm">
            <v-icon left>mdi-check</v-icon>Yes
          </v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible = false">
            <v-icon left>mdi-close</v-icon>No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="successSnackbar" color="success" timeout="3000">
      {{ successMessage }}
      <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, doc, getDoc, updateDoc, collection, 
  getDocs, query, where, runTransaction
} from "firebase/firestore";
import { firebaseApp, auth } from "@/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const db = getFirestore(firebaseApp);

export default {
  name: "EditShipment",
  data() {
    return {
      loading: false,
      valid: false,
      shipmentId: "",
      shipmentNumber: "",
      createdDate: "",
      shipmentOrderNumber: "",
      poNumber: "",
      supplierName: "",
      customerName: "",
      arrivalDate: "",
      arrivalDateMenu: false,
      method: "",
      status: "",
      linkedSONumbers: [],
      latestUpdateFromSupplier: [],
      newSupplierUpdate: "",
      currentUser: null,
      isViewOnly: false,
      authStateUnsubscribe: null,
      
      supplierOptions: [],
      customerOptions: [],
      availableSOs: [],
      
      methodOptions: ["Air", "Sea", "Road"],
      statusOptions: [
        "Open Order", "Referenced", "Booked", "Manufacturing",
        "In Transit", "Docked", "At Airport", "On Route to JHB",
        "Cancelled", "Delivered"
      ],
      
      soManagementDialog: {
        visible: false,
        activeTab: 0,
        newSOs: []
      },
      
      isOpenOrderShipment: false,
      
      confirmationDialog: { 
        visible: false, title: "", message: "", confirm: () => {} 
      },
      successSnackbar: false,
      successMessage: "",
      errorSnackbar: false,
      errorMessage: "",
      
      rules: { 
        required: (v) => !!v || "Required.",
        requiredArray: (v) => (v && v.length > 0) || "At least one SO must be linked."
      },
      
      tableHeaders: [
        { text: "Date & Time", value: "timestamp", width: "180px" },
        { text: "Update", value: "text", width: "400px" },
        { text: "User", value: "user", width: "120px", align: "center" },
        { text: "Actions", value: "actions", sortable: false, width: "80px", align: "center" },
      ],
      
      soTableHeaders: [
        { text: "SO Number", value: "salesOrderNumber", width: "140px" },
        { text: "Customer", value: "customerName", width: "200px" },
        { text: "Status", value: "status", width: "150px" },
        { text: "Actions", value: "actions", sortable: false, width: "80px", align: "center" },
      ],
      
      isAdmin: false,
    };
  },
  
  computed: {
    linkedSODetails() {
      if (!this.linkedSONumbers || !Array.isArray(this.linkedSONumbers)) {
        return [];
      }
      
      if (!this.availableSOs || !Array.isArray(this.availableSOs)) {
        return [];
      }
      
      const tableData = [];
      
      for (let i = 0; i < this.linkedSONumbers.length; i++) {
        const soNumber = this.linkedSONumbers[i];
        
        const matchingSO = this.availableSOs.find(so => 
          so.salesOrderNumber === soNumber || 
          so.salesOrderNumber === soNumber.toString() ||
          so.salesOrderNumber.toString() === soNumber.toString()
        );
        
        const rowData = {
          salesOrderNumber: soNumber,
          customerName: matchingSO?.customerName || 'Unknown Customer',
          status: matchingSO?.status || 'Unknown Status'
        };
        
        tableData.push(rowData);
      }
      
      return tableData;
    }
  },
  
  async created() {
    this.loading = true;
    
    const viewMode = localStorage.getItem("shipmentViewMode");
    this.isViewOnly = viewMode === "view-only";
    
    this.authStateUnsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.$router.push('/login');
        return;
      }
      
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          this.isAdmin = data.role === "Admin";
          this.currentUser = data.name || user.email || "Unknown User";
        }
      } catch (e) {
        this.handleError(e, "Loading user data");
      }
    });

    try {
      const shipmentId = localStorage.getItem("selectedShipmentId");
      if (!shipmentId) {
        this.errorMessage = "No shipment selected.";
        this.errorSnackbar = true;
        this.$router.go(-1);
        return;
      }
      this.shipmentId = shipmentId;

      await Promise.all([
        this.fetchShipmentData(),
        this.fetchSupplierOptions(),
        this.fetchCustomerOptions(),
        this.fetchAvailableSOs()
      ]);

    } catch (e) {
      this.handleError(e, "Initializing form");
    } finally {
      this.loading = false;
    }
  },
  
  beforeDestroy() {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }
  },
  
  methods: {
    handleError(error, operation) {
      console.error(`Error in ${operation}:`, error);
      
      if (error.code === 'permission-denied' || error.code === 'unauthenticated') {
        this.$router.push('/login');
        return;
      }
      
      this.errorMessage = `${operation} failed: ${error.message}`;
      this.errorSnackbar = true;
    },

    async fetchShipmentData() {
      try {
        const docRef = doc(db, "shipments", this.shipmentId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const d = docSnap.data();
          
          this.isOpenOrderShipment = d.shipmentNumber === 'OPEN_ORDER';
          
          this.shipmentNumber = d.shipmentNumber || "";
          this.createdDate = d.createdDate ? new Date(d.createdDate).toLocaleDateString() : "";
          this.shipmentOrderNumber = d.shipmentOrderNumber || "";
          this.poNumber = d.poNumber || "";
          this.supplierName = d.supplierName || "";
          this.customerName = d.customerName || "";
          this.arrivalDate = d.expectedArrivalDate || "";
          this.method = d.method || "";
          this.status = d.status || "";
          this.linkedSONumbers = d.linkedSONumbers || [];
          this.latestUpdateFromSupplier = Array.isArray(d.latestUpdateFromSupplier)
            ? d.latestUpdateFromSupplier
            : [];
        } else {
          throw new Error("Shipment not found");
        }
      } catch (e) {
        this.handleError(e, "Loading shipment data");
        this.$router.go(-1);
      }
    },

    async fetchSupplierOptions() {
      try {
        const col = collection(db, "suppliers");
        const snap = await getDocs(col);
        
        this.supplierOptions = snap.docs
          .map((d) => {
            const data = d.data();
            const supplierName = data["Supplier Name"] || 
                               data["supplierName"] || 
                               data["name"] || 
                               data["Name"] || 
                               data["supplier_name"] || "";
            return { supplierName };
          })
          .filter((s) => s.supplierName && s.supplierName.trim() !== "")
          .sort((a, b) => a.supplierName.localeCompare(b.supplierName));
        
        if (this.supplierOptions.length === 0) {
          const altCol = collection(db, "supplier");
          const altSnap = await getDocs(altCol);
          
          this.supplierOptions = altSnap.docs
            .map((d) => {
              const data = d.data();
              const supplierName = data["Supplier Name"] || 
                                 data["supplierName"] || 
                                 data["name"] || "";
              return { supplierName };
            })
            .filter((s) => s.supplierName && s.supplierName.trim() !== "")
            .sort((a, b) => a.supplierName.localeCompare(b.supplierName));
        }
      } catch (e) {
        this.handleError(e, "Loading suppliers");
      }
    },

    async fetchCustomerOptions() {
      try {
        const col = collection(db, "customers");
        const snap = await getDocs(col);
        
        this.customerOptions = snap.docs
          .map((d) => {
            const data = d.data();
            const customerName = data["Customer Name"] || 
                                data["customerName"] || 
                                data["name"] || 
                                data["Name"] || 
                                data["customer_name"] || "";
            return { customerName };
          })
          .filter((c) => c.customerName && c.customerName.trim() !== "")
          .sort((a, b) => a.customerName.localeCompare(b.customerName));
        
        if (this.customerOptions.length === 0) {
          const altCol = collection(db, "customer");
          const altSnap = await getDocs(altCol);
          
          this.customerOptions = altSnap.docs
            .map((d) => {
              const data = d.data();
              const customerName = data["Customer Name"] || 
                                  data["customerName"] || 
                                  data["name"] || 
                                  data["Name"] || "";
              return { customerName };
            })
            .filter((c) => c.customerName && c.customerName.trim() !== "")
            .sort((a, b) => a.customerName.localeCompare(b.customerName));
        }
      } catch (e) {
        this.handleError(e, "Loading customers");
      }
    },

    async fetchAvailableSOs() {
      try {
        const col = collection(db, "SalesTracker");
        const snap = await getDocs(col);
        
        this.availableSOs = snap.docs
          .map((d) => {
            const data = d.data();
            
            const salesOrderNumber = data.salesOrderNumber || 
                                   data["Sales Order Number"] || 
                                   data["salesOrderNo"] || 
                                   data["so_number"] || "";
            const supplierName = data.supplierName || 
                               data["Supplier Name"] || 
                               data["supplier"] || "";
            const customerName = data.customerName || 
                               data["Customer Name"] || 
                               data["customer"] || 
                               data["Customer"] ||
                               data["client"] ||
                               data["Client"] ||
                               data["clientName"] || "";
            const status = data.status || data["Status"] || "";
            
            return {
              salesOrderNumber: salesOrderNumber.toString(),
              supplierName,
              customerName,
              status,
              soDisplay: `${salesOrderNumber} - ${customerName || 'Unknown Customer'}`,
              docId: d.id
            };
          })
          .filter((so) => so.salesOrderNumber && so.salesOrderNumber.trim() !== "")
          .filter((so) => so.status !== "Delivered" && so.status !== "Complete")
          .sort((a, b) => {
            const aNum = parseInt(a.salesOrderNumber, 10);
            const bNum = parseInt(b.salesOrderNumber, 10);
            return aNum - bNum;
          });
        
        if (this.availableSOs.length === 0) {
          const altCol = collection(db, "salesTracker");
          const altSnap = await getDocs(altCol);
          
          this.availableSOs = altSnap.docs
            .map((d) => {
              const data = d.data();
              const salesOrderNumber = data.salesOrderNumber || 
                                     data["Sales Order Number"] || 
                                     data["so_number"] || "";
              const supplierName = data.supplierName || 
                                 data["Supplier Name"] || "";
              const customerName = data.customerName || 
                                 data["Customer Name"] || "";
              const status = data.status || "";
              
              return {
                salesOrderNumber: salesOrderNumber.toString(),
                supplierName,
                customerName,
                status,
                soDisplay: `${salesOrderNumber} - ${customerName || 'Unknown Customer'}`,
                docId: d.id
              };
            })
            .filter((so) => so.salesOrderNumber && so.salesOrderNumber.trim() !== "")
            .filter((so) => so.status !== "Delivered" && so.status !== "Complete")
            .sort((a, b) => parseInt(a.salesOrderNumber, 10) - parseInt(b.salesOrderNumber, 10));
        }
      } catch (e) {
        this.handleError(e, "Loading sales orders");
      }
    },

    openSOManagementDialog() {
      this.soManagementDialog.visible = true;
      this.soManagementDialog.activeTab = 0;
      this.soManagementDialog.newSOs = [];
    },

    getSOSupplier(soNumber) {
      const so = this.availableSOs.find(s => s.salesOrderNumber === soNumber);
      return so ? (so.customerName || 'Unknown Customer') : 'Unknown Customer';
    },

    confirmRemoveSOFromShipment(soNumber) {
      this.confirmationDialog = {
        visible: true,
        title: "Remove SO from Shipment",
        message: `Remove SO #${soNumber} from this shipment? It will be returned to Open Order.`,
        confirm: () => this.removeSOFromShipment(soNumber)
      };
    },

    async removeSOFromShipment(soNumber) {
      try {
        this.linkedSONumbers = this.linkedSONumbers.filter(so => so !== soNumber);
        await this.addSOToOpenOrder(soNumber);
        
        this.successMessage = `SO #${soNumber} removed and returned to Open Order`;
        this.successSnackbar = true;
        
      } catch (error) {
        this.handleError(error, 'Removing SO from shipment');
      }
      
      this.confirmationDialog.visible = false;
    },

    async addSOToOpenOrder(soNumber) {
      try {
        const openOrderQuery = query(
          collection(db, 'shipments'), 
          where('shipmentNumber', '==', 'OPEN_ORDER')
        );
        const snapshot = await getDocs(openOrderQuery);
        
        if (!snapshot.empty) {
          const openOrderDoc = snapshot.docs[0];
          const currentLinkedSOs = openOrderDoc.data().linkedSONumbers || [];
          
          if (!currentLinkedSOs.includes(soNumber)) {
            await updateDoc(openOrderDoc.ref, {
              linkedSONumbers: [...currentLinkedSOs, soNumber]
            });
          }
        }
      } catch (error) {
        this.handleError(error, "Adding SO to Open Order");
      }
    },

    async addSOsToShipment() {
      const newSOs = this.soManagementDialog.newSOs.filter(
        so => !this.linkedSONumbers.includes(so)
      );
      
      this.linkedSONumbers = [...this.linkedSONumbers, ...newSOs];
      
      if (!this.isOpenOrderShipment && newSOs.length > 0) {
        await this.removeSOsFromOpenOrder(newSOs);
      }
      
      this.soManagementDialog.newSOs = [];
      this.soManagementDialog.visible = false;
      
      this.successMessage = `Added ${newSOs.length} SOs to shipment`;
      this.successSnackbar = true;
    },

    async removeSOsFromOpenOrder(soNumbers) {
      try {
        const openOrderQuery = query(
          collection(db, 'shipments'), 
          where('shipmentNumber', '==', 'OPEN_ORDER')
        );
        const snapshot = await getDocs(openOrderQuery);
        
        if (!snapshot.empty) {
          const openOrderDoc = snapshot.docs[0];
          const currentLinkedSOs = openOrderDoc.data().linkedSONumbers || [];
          
          const updatedLinkedSOs = currentLinkedSOs.filter(
            so => !soNumbers.includes(so)
          );
          
          await updateDoc(openOrderDoc.ref, {
            linkedSONumbers: updatedLinkedSOs
          });
        }
      } catch (error) {
        this.handleError(error, "Removing SOs from Open Order");
      }
    },

    removeSO(soNumber) {
      this.linkedSONumbers = this.linkedSONumbers.filter(so => so !== soNumber);
    },

    updateLinkedSOStatuses() {
      // This will be called when status changes
    },

    confirmAddSupplierUpdate() {
      if (!this.newSupplierUpdate.trim()) {
        this.errorMessage = "Update text cannot be empty.";
        this.errorSnackbar = true;
        return;
      }
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Supplier Update",
        message: "Add this supplier update?",
        confirm: this.addSupplierUpdate,
      };
    },

    addSupplierUpdate() {
      if (!Array.isArray(this.latestUpdateFromSupplier)) {
        this.latestUpdateFromSupplier = [];
      }
      const update = {
        text: this.newSupplierUpdate,
        timestamp: new Date().toISOString(),
        user: this.currentUser || "Unknown User",
      };
      this.latestUpdateFromSupplier.push(update);
      this.newSupplierUpdate = "";
      this.successMessage = "Supplier update added successfully!";
      this.successSnackbar = true;
      this.confirmationDialog.visible = false;
    },

    removeUpdate(index) {
      this.latestUpdateFromSupplier.splice(index, 1);
    },

    confirmSubmit() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Update",
        message: `Are you sure you want to save changes to Shipment #${this.shipmentNumber}?`,
        confirm: this.updateForm,
      };
    },

    async updateForm() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      
      try {
        const docRef = doc(db, "shipments", this.shipmentId);
        
        await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(docRef);
          if (!docSnap.exists()) {
            throw new Error("Shipment no longer exists");
          }
          
          transaction.update(docRef, {
            shipmentOrderNumber: this.shipmentOrderNumber,
            poNumber: this.poNumber,
            supplierName: this.supplierName,
            customerName: this.customerName,
            expectedArrivalDate: this.arrivalDate,
            method: this.method,
            status: this.status,
            linkedSONumbers: this.linkedSONumbers,
            latestUpdateFromSupplier: this.latestUpdateFromSupplier,
            lastModified: new Date().toISOString(),
            lastModifiedBy: this.currentUser || "Unknown User"
          });
        });
        
        if (!this.isOpenOrderShipment) {
          await this.removeSOsFromOpenOrder(this.linkedSONumbers);
        }
        
        await this.updateLinkedSOStatusesInDB();
        await this.removeSOsFromOpenOrder(this.linkedSONumbers);
        await this.removeDeliveredSOsFromOpenOrder();
        
        this.successMessage = `Shipment #${this.shipmentNumber} updated successfully!`;
        this.successSnackbar = true;
        
        setTimeout(() => {
          this.$router.push({ name: "AllShipment" });
        }, 1500);
        
      } catch (e) {
        this.handleError(e, "Updating shipment");
      } finally {
        this.loading = false;
        this.confirmationDialog.visible = false;
      }
    },

    async updateLinkedSOStatusesInDB() {
      try {
        for (const soNumber of this.linkedSONumbers) {
          const soDoc = this.availableSOs.find(so => so.salesOrderNumber === soNumber);
          if (soDoc) {
            const shipmentStatuses = await this.getSOShipmentStatuses(soNumber);
            let newStatus;
            
            if (shipmentStatuses.length > 1) {
              const allDelivered = shipmentStatuses.every(s => s === "Delivered");
              newStatus = allDelivered ? "Delivered" : "Partial Supply";
            } else if (shipmentStatuses.length === 1) {
              newStatus = this.status === "Delivered" ? "Delivered" : this.status;
            } else {
              newStatus = "Open Order";
            }
            
            await runTransaction(db, async (transaction) => {
              const soDocRef = doc(db, "SalesTracker", soDoc.docId);
              const soDocSnap = await transaction.get(soDocRef);
              if (soDocSnap.exists()) {
                const soData = soDocSnap.data();
                const supplierUpdates = soData.supplierUpdates || [];
                
                const statusUpdate = {
                  text: `Status updated to "${newStatus}" from Shipment #${this.shipmentNumber}`,
                  timestamp: new Date().toISOString(),
                  user: this.currentUser || "System",
                  source: 'shipment_status_change',
                  shipmentId: this.shipmentId,
                  shipmentNumber: this.shipmentNumber
                };
                
                supplierUpdates.push(statusUpdate);
                
                transaction.update(soDocRef, {
                  status: newStatus,
                  supplierUpdates: supplierUpdates
                });
              }
            });
          }
        }
      } catch (e) {
        this.handleError(e, "Updating linked SO statuses");
      }
    },

    async getSOShipmentStatuses(soNumber) {
      try {
        const shipmentsCol = collection(db, "shipments");
        const allShipmentsSnapshot = await getDocs(shipmentsCol);
        
        const statuses = [];
        
        allShipmentsSnapshot.forEach(doc => {
          const data = doc.data();
          const linkedSOs = data.linkedSONumbers || [];
          
          if (data.shipmentNumber !== "OPEN_ORDER" && linkedSOs.includes(soNumber)) {
            statuses.push(data.status);
          }
        });
        
        return statuses;
      } catch (error) {
        this.handleError(error, "Getting SO shipment statuses");
        return [];
      }
    },

    async removeSOsFromOpenOrder(soNumbers) {
      try {
        const openOrderQuery = query(
          collection(db, 'shipments'), 
          where('shipmentNumber', '==', 'OPEN_ORDER')
        );
        const snapshot = await getDocs(openOrderQuery);
        
        if (!snapshot.empty) {
          const openOrderDoc = snapshot.docs[0];
          const currentLinkedSOs = openOrderDoc.data().linkedSONumbers || [];
          
          // Remove the SOs that are now on this shipment
          const updatedLinkedSOs = currentLinkedSOs.filter(
            so => !soNumbers.includes(so)
          );
          
          await updateDoc(openOrderDoc.ref, {
            linkedSONumbers: updatedLinkedSOs
          });
        }
      } catch (error) {
        this.handleError(error, "Removing SOs from Open Order");
      }
    },

    async removeDeliveredSOsFromOpenOrder() {
      try {
        const openOrderQuery = query(
          collection(db, 'shipments'), 
          where('shipmentNumber', '==', 'OPEN_ORDER')
        );
        const snapshot = await getDocs(openOrderQuery);
        
        if (!snapshot.empty) {
          const openOrderDoc = snapshot.docs[0];
          const currentLinkedSOs = openOrderDoc.data().linkedSONumbers || [];
          
          // Remove any SOs that are now delivered
          const deliveredSOs = [];
          for (const soNumber of this.linkedSONumbers) {
            const soDoc = this.availableSOs.find(so => so.salesOrderNumber === soNumber);
            if (soDoc) {
              const shipmentStatuses = await this.getSOShipmentStatuses(soNumber);
              if (shipmentStatuses.length === 1 && this.status === "Delivered") {
                deliveredSOs.push(soNumber);
              } else if (shipmentStatuses.length > 1) {
                const allDelivered = shipmentStatuses.every(s => s === "Delivered");
                if (allDelivered) {
                  deliveredSOs.push(soNumber);
                }
              }
            }
          }
          
          // Remove delivered SOs from Open Order
          const updatedLinkedSOs = currentLinkedSOs.filter(
            so => !deliveredSOs.includes(so)
          );
          
          if (updatedLinkedSOs.length !== currentLinkedSOs.length) {
            await updateDoc(openOrderDoc.ref, {
              linkedSONumbers: updatedLinkedSOs
            });
          }
        }
      } catch (error) {
        this.handleError(error, "Removing delivered SOs from Open Order");
      }
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: "Confirm Navigation",
        message: "Unsaved changes will be lost. Go back?",
        confirm: this.goBack,
      };
    },

    goBack() {
      this.confirmationDialog.visible = false;
      localStorage.removeItem("shipmentViewMode");
      this.$router.go(-1);
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    refreshTableData() {
      this.$forceUpdate();
    },

    getStatusColor(status) {
      const colors = {
        'Open Order': 'indigo',
        'Referenced': 'orange',
        'Booked': 'purple',
        'Manufacturing': 'amber',
        'Partial Supply': 'deep-orange',
        'In Transit': 'green',
        'Docked': 'teal',
        'At Airport': 'cyan',
        'On Route to JHB': 'light-green',
        'Delivered': 'success',
        'Complete': 'success',
        'Cancelled': 'error'
      };
      return colors[status] || 'grey';
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");

.nexus-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.nexus-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.nexus-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.nexus-alert {
  border-radius: 12px !important;
  background: rgba(25, 118, 210, 0.1) !important;
  border-left: 4px solid #1976d2 !important;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-outlined {
  border: 2px solid #475569 !important;
  color: #475569 !important;
  background: transparent !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-outlined:hover {
  background: #475569 !important;
  color: white !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.3) !important;
}

.nexus-btn-primary {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 16px !important;
  font-weight: 700 !important;
  letter-spacing: 1px !important;
  text-transform: uppercase !important;
  font-size: 1.1rem !important;
  padding: 16px 32px !important;
  box-shadow: 
    0 10px 30px 0 rgba(71, 85, 105, 0.4),
    0 0 0 1px rgba(71, 85, 105, 0.2) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-primary:hover {
  transform: translateY(-3px) !important;
  box-shadow: 
    0 15px 40px 0 rgba(71, 85, 105, 0.5),
    0 0 0 1px rgba(71, 85, 105, 0.3) !important;
}

.nexus-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.nexus-table {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-table >>> .v-data-table__wrapper {
  border-radius: 16px !important;
}

.nexus-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
}

.nexus-table >>> .v-data-table-header th {
  color: #334155 !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.back-button .v-icon {
  color: white !important;
}

.nexus-input >>> .v-messages__message {
  color: #dc2626 !important;
  font-weight: 500 !important;
}

.nexus-input >>> .v-input--has-state .v-input__slot {
  border-color: #dc2626 !important;
}

@media (max-width: 768px) {
  .nexus-title {
    font-size: 2rem;
    letter-spacing: 1px;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .nexus-card {
    margin: 10px;
    border-radius: 16px;
  }
  
  .nexus-card >>> .v-card__text {
    padding: 20px !important;
  }
}
</style>